/**
 * Does the "Zonas de Acesso" table describe access this person actually has?
 *
 * The table comes from `effective-access-zones`, which resolves profile assignments —
 * person_type, unit, department — and applies NO state rule at all. The backend's two real gates
 * do:
 *
 *   ZoneService.GetAuthorizedPeople       is_active AND is_authorized AND, for a visitor,
 *                                         visitor_state IN (awaiting_entry, inside)
 *   DeviceZoneSyncService.getPeopleByType the same, plus today_entry_granted for a prestador
 *
 * So a visitante still marked "Pendente" was shown five zones she had never been released into,
 * above a Dispositivos table that said "Sem credencial" on every row — the screen contradicting
 * itself, and the enforcement. Nothing was ever written to a device; what was missing was the
 * screen saying so.
 *
 * This mirrors the two rules above rather than replacing them: it is a caption on a table, never
 * authorization. The honest fix is for the endpoint to answer it, which needs a backend deploy;
 * this ships today and stays correct either way, because it only ever says LESS than the table.
 *
 * See accessInEffect.test.ts.
 */

/** The states in which the portaria has actually opened someone's entry window. */
const RELEASED_STATES = ['awaiting_entry', 'inside'];

function metadataOf(role: any): Record<string, any> {
  const meta = role?.role_metadata;
  return meta && typeof meta === 'object' ? meta : {};
}

/** Mirrors the server: does THIS role put the person on a device right now? */
function grantsAccessNow(role: any): boolean {
  if (!role?.is_active || !role?.is_authorized) return false;

  const state = metadataOf(role).visitor_state;
  if (role.role_type === 'visitor') return RELEASED_STATES.includes(state);
  if (role.role_type === 'service_provider') {
    return RELEASED_STATES.includes(state) && metadataOf(role).today_entry_granted === true;
  }
  // resident, staff, ... — no entry window, the role applies as soon as it is authorised.
  return true;
}

const SUFFIX = 'as zonas abaixo não concedem acesso ainda';

/**
 * Why the zones below are not in force — or null when they are (or when there is nothing to
 * judge, so a person mid-registration does not get a warning about an empty table).
 *
 * One reason for the whole person, not one per role: the table is the person's, and a single
 * working role is enough for it to be true.
 */
export function accessNotInEffectReason(roles: any[] | null | undefined): string | null {
  const list = Array.isArray(roles) ? roles.filter(Boolean) : [];
  if (list.length === 0) return null;
  if (list.some(grantsAccessNow)) return null;

  // The most specific thing that is true, reading the way the row above it already reads
  // ("● Ativa  Pendente").
  const active = list.filter((role) => role?.is_active);
  if (active.length === 0) return `Nenhuma função ativa — ${SUFFIX}`;

  if (active.some((role) => !role?.is_authorized)) {
    return `Função pendente de autorização — ${SUFFIX}`;
  }

  const prestador = active.find((role) => role?.role_type === 'service_provider');
  if (prestador && metadataOf(prestador).today_entry_granted !== true) {
    return `Entrada de hoje ainda não liberada — ${SUFFIX}`;
  }

  // Authorised, but the portaria has not released the visit: approving in the app is not what
  // opens a gate, and the role stays at pre_registered until the release runs.
  return `Visita ainda não liberada na portaria — ${SUFFIX}`;
}
